<template>
   <div>
      <h6 class="sub-heading">Deploying XSurfLog via Windows Group Policy</h6>
      <p>Use Group Policy to silently install the XSurfLog extension across all managed devices in your organisation. This is the recommended method for enterprise environments running Windows with Active Directory.</p>

      <div class="info-box info-box-blue mb-3">
         <i class="fa-solid fa-circle-info me-2"></i>
         <strong>Prerequisite:</strong> You must have the Google Chrome ADMX/ADML administrative templates imported into your Group Policy environment. Download them from
         <a href="https://chromeenterprise.google/browser/download/" target="_blank">chromeenterprise.google</a>.
      </div>

      <h6 class="sub-heading mt-4">Step 1: Get the Extension ID and Update URL</h6>
      <p>Every Chrome extension has a unique ID and an update URL. For XSurfLog, these are:</p>
      <div class="code-block">
         <code>mipgaapolfgfbonecakhhacpmafffogo;https://clients2.google.com/service/update2/crx</code>
      </div>
      <p class="mt-2">The first part (<code>mipgaapolfgfbonecakhhacpmafffogo</code>) is the XSurfLog extension ID. The second part after the semicolon is the Chrome Web Store update URL, which is the same for all store-published extensions.</p>

      <h6 class="sub-heading mt-4">Step 2: Open Group Policy Editor</h6>
      <ol class="step-list">
         <li>Open the <strong>Group Policy Management Console</strong> on your domain controller.</li>
         <li>Create a new GPO or edit an existing one that applies to the desired users/computers.</li>
         <li>Navigate to: <strong>Computer Configuration → Administrative Templates → Google → Google Chrome → Extensions</strong></li>
         <li>Double-click <strong>"Configure the list of force-installed apps and extensions"</strong> and set it to <strong>Enabled</strong>.</li>
      </ol>

      <h6 class="sub-heading mt-4">Step 3: Add the Extension String</h6>
      <ol class="step-list">
         <li>Click <strong>Show</strong> in the policy settings dialog.</li>
         <li>In the <strong>Value</strong> field, paste the extension string:</li>
      </ol>
      <div class="code-block">
         <code>mipgaapolfgfbonecakhhacpmafffogo;https://clients2.google.com/service/update2/crx</code>
      </div>
      <ol class="step-list" start="3">
         <li>Click <strong>OK</strong> and close the editor.</li>
      </ol>

      <h6 class="sub-heading mt-4">Step 4: Verify Deployment</h6>
      <p>On the next Group Policy refresh cycle (or by running <code>gpupdate /force</code> on a client machine), the XSurfLog extension will be automatically installed for all users the policy applies to. The installation runs silently with no user interaction required.</p>

      <div class="info-box info-box-yellow mb-3">
         <i class="fa-solid fa-triangle-exclamation me-2"></i>
         <strong>Important:</strong> Ensure that Developer Mode is <strong>disabled</strong> in Chrome's extension settings. Force-installed extensions may not deploy correctly when Developer Mode is enabled.
      </div>

      <h6 class="sub-heading mt-4">For Microsoft Edge</h6>
      <p>The same process applies for Microsoft Edge (Chromium-based). Navigate to <strong>Administrative Templates → Microsoft Edge → Extensions</strong> and use the Edge extension string:</p>
      <div class="code-block">
         <code>hkippnibankfefilphlobakcabmplobj;https://edge.microsoft.com/extensionwebstorebase/v1/crx</code>
      </div>

      <p class="mt-3 ref-text">
         <i class="fa-solid fa-link me-1"></i> Reference:
         <a href="https://dennisspan.com/deploying-google-chrome-extensions-using-group-policy/" target="_blank">
            dennisspan.com — Deploying Chrome Extensions via Group Policy
         </a>
      </p>
   </div>
</template>

<style scoped>
.sub-heading {
   font-weight: 700; font-size: 15px; color: #435666;
}

p, li { font-size: 14px; color: #435666; line-height: 1.7; }

.code-block {
   background: #f5f6f8; border: 1px solid #e9ecef;
   border-radius: 8px; padding: 12px 16px;
   font-family: monospace; font-size: 13px;
   word-break: break-all; color: #d63384;
}

.info-box {
   border-radius: 8px; padding: 12px 16px;
   font-size: 13px; line-height: 1.6;
}
.info-box-blue { background: #e7f6f9; border-left: 4px solid #00A0C1; color: #435666; }
.info-box-yellow { background: #fff8e1; border-left: 4px solid #ffc107; color: #435666; }

.step-list { padding-left: 20px; }
.step-list li { margin-bottom: 6px; }

.ref-text { font-size: 13px; color: #6c757d; }
.ref-text a { color: #00A0C1; text-decoration: none; }
.ref-text a:hover { text-decoration: underline; }
</style>
